import React, { Suspense, useState } from 'react'
import { gql, useQuery } from 'urql'
import {
  Alert as MUIAlert,
  Box,
  Button,
  Chip,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import {
  Email,
  PauseCircle,
  Phone,
  Replay,
  Sms,
  Webhook,
} from '@mui/icons-material'
import { pathPrefix } from '../env'
import AppLink from '../util/AppLink'
import { UserAvatar } from '../util/avatars'
import { DestinationAvatar } from '../util/DestinationAvatar'
import { useSessionInfo } from '../util/RequireConfig'
import { QuerySetFavoriteButton } from '../util/QuerySetFavoriteButton'
import { GenericError, ObjectNotFound } from '../error-pages'
import UserContactMethodVerificationDialog from '../users/UserContactMethodVerificationDialog'
import SendTestDialog from '../users/SendTestDialog'
import UserShiftsCalendar from '../users/UserShiftsCalendar'
import { useIsWidthDown } from '../util/useWidth'
import { serif } from './brand'
import { CallPause, getCallPauses, pauseEnd, usePolled } from './api'
import { IconTile, Page, Panel, Row, useBrand } from './ui'
import { usePlaces, useTimezones, zoneName } from './tz'
import { DateTime } from 'luxon'

// GoAlert's urql client suspends by default; this page renders its own states.
const noSuspense = { suspense: false }

const query = gql`
  query ErecordsUserProfile($id: ID!) {
    user(id: $id) {
      id
      name
      email
      role
      contactMethods {
        id
        name
        disabled
        pending
        dest {
          type
          displayInfo {
            ... on DestinationDisplayInfo {
              text
              iconURL
              iconAltText
            }
            ... on DestinationDisplayInfoError {
              error
            }
          }
        }
      }
      notificationRules {
        id
        contactMethodID
      }
      onCallOverview {
        serviceCount
        serviceAssignments {
          stepNumber
          escalationPolicyID
          escalationPolicyName
          serviceID
          serviceName
        }
      }
    }
  }
`

type CM = {
  id: string
  name: string
  disabled: boolean
  pending: boolean
  dest: {
    type: string
    displayInfo: {
      text?: string
      iconURL?: string
      iconAltText?: string
      error?: string
    }
  }
}

async function post(path: string, body?: unknown): Promise<void> {
  const res = await fetch(pathPrefix + '/api/v2/erecords/' + path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok)
    throw new Error((await res.text()).trim() || `HTTP ${res.status}`)
}

// cleanName drops the "- paused till 1430Z" suffix a pause adds to the name.
function cleanName(n: string): string {
  return n
    .replace(/ - (calls )?paused( till \d{4}Z)?$/, '')
    .replace(/\s*\(Entra sync\)\s*/i, '')
}

const PHONE_TYPES = ['builtin-twilio-voice', 'builtin-twilio-sms']

// kindLabel names what a contact method does; the number is shown once above.
function kindLabel(cm: CM): string {
  if (cm.dest.type === 'builtin-twilio-voice') return 'Phone calls'
  if (cm.dest.type === 'builtin-twilio-sms') return 'Text messages (SMS)'
  if (cm.dest.type === 'erecords-teams-chat') return 'Teams message'
  if (cm.dest.type === 'erecords-teams-call') return 'Teams call'
  return cleanName(cm.name)
}

function pausedLabel(p: CallPause | undefined): string {
  if (!p) return 'Paused'
  return `Paused · ${pauseEnd(p.until)}`
}

// TimeZoneLine shows the person's time zone (from their Microsoft Entra
// contact information, set by the sync), used to show times in their clock.
function TimeZoneLine(props: { userID: string }): React.JSX.Element {
  const c = useBrand()
  const zone = useTimezones()[props.userID]
  const place = usePlaces()[props.userID]
  return (
    <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.5 }}>
      Time zone:{' '}
      {zone ? (
        <React.Fragment>
          <b style={{ color: c.ink }}>{zoneName(zone)}</b>
          {place ? ` · ${place}` : ''} · now{' '}
          {DateTime.local().setZone(zone).toFormat('HH:mm')} there
        </React.Fragment>
      ) : (
        <React.Fragment>
          <b style={{ color: c.ink }}>not set</b> · needs a country in the
          contact information
        </React.Fragment>
      )}
    </Typography>
  )
}

// CMIcon shows the contact method's kind: phone call, SMS, email, Teams, webhook.
function CMIcon({ cm }: { cm: CM }): React.JSX.Element {
  const icon: Record<string, React.ReactNode> = {
    'builtin-twilio-voice': <Phone />,
    'builtin-twilio-sms': <Sms />,
    'builtin-smtp-email': <Email />,
    'builtin-webhook': <Webhook />,
  }
  const i = icon[cm.dest.type]
  if (i) {
    return <IconTile tone={cm.disabled ? 'muted' : 'brand'}>{i}</IconTile>
  }
  return (
    <DestinationAvatar
      iconURL={cm.dest.displayInfo.iconURL}
      iconAltText={cm.dest.displayInfo.iconAltText}
      error={Boolean(cm.dest.displayInfo.error)}
    />
  )
}

// UserProfile replaces upstream's user details: contact methods can be paused
// but not removed, and there are no notification rules to manage (every
// contact method notifies immediately; adding one adds its rule).
export default function UserProfile(props: {
  userID: string
}): React.JSX.Element {
  const c = useBrand()
  const mobile = useIsWidthDown('md')
  const { userID: me, isAdmin } = useSessionInfo()
  const canEdit = isAdmin || me === props.userID
  const [q, refetch] = useQuery({
    query,
    variables: { id: props.userID },
    context: noSuspense,
  })
  const pauses = usePolled(getCallPauses, 30_000)

  const [verifyID, setVerifyID] = useState<string>()
  const [testID, setTestID] = useState<string>()
  const [menu, setMenu] = useState<{ el: HTMLElement; cm: CM }>()
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const user = q.data?.user
  if (q.error) return <GenericError error={q.error.message} />
  if (!user) {
    return q.fetching ? (
      <Typography sx={{ p: 3, color: c.muted }}>Loading…</Typography>
    ) : (
      <ObjectNotFound />
    )
  }

  const reload = (): void => {
    refetch({ requestPolicy: 'network-only' })
    pauses.refresh()
  }
  const act = (fn: () => Promise<void>): void => {
    setBusy(true)
    setErr('')
    setMenu(undefined)
    fn()
      .then(reload)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false))
  }

  const cms: CM[] = user.contactMethods
  // calls and SMS go to the same Entra number: show it once, the two kinds under it
  const groups: { key: string; number?: string; cms: CM[] }[] = []
  for (const cm of cms) {
    const number = PHONE_TYPES.includes(cm.dest.type)
      ? cm.dest.displayInfo.text
      : undefined
    const g = number && groups.find((x) => x.number === number)
    if (g) g.cms.push(cm)
    else groups.push({ key: number ?? cm.id, number, cms: [cm] })
  }
  for (const g of groups)
    g.cms.sort((a, b) => a.dest.type.localeCompare(b.dest.type)).reverse()
  const ruled = new Set(
    user.notificationRules.map(
      (r: { contactMethodID: string }) => r.contactMethodID,
    ),
  )
  const assignments = user.onCallOverview.serviceAssignments as {
    stepNumber: number
    escalationPolicyID: string
    escalationPolicyName: string
    serviceID: string
    serviceName: string
  }[]

  return (
    <Page
      title={user.name}
      subtitle={
        <React.Fragment>
          {user.email}
          {user.role === 'admin' && ' · Admin'}
        </React.Fragment>
      }
      maxWidth={1000}
      actions={
        <React.Fragment>
          <QuerySetFavoriteButton id={props.userID} type='user' />
        </React.Fragment>
      }
    >
      <Suspense>
        {verifyID && (
          <UserContactMethodVerificationDialog
            contactMethodID={verifyID}
            onClose={() => {
              setVerifyID(undefined)
              reload()
            }}
          />
        )}
        {testID && (
          <SendTestDialog
            contactMethodID={testID}
            onClose={() => setTestID(undefined)}
          />
        )}
      </Suspense>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
        <UserAvatar
          userID={props.userID}
          sx={{ width: 72, height: 72, fontSize: 28 }}
        />
        <Box>
          {user.onCallOverview.serviceCount > 0 ? (
            <Chip
              color='primary'
              label={`On call now · ${user.onCallOverview.serviceCount} service${user.onCallOverview.serviceCount === 1 ? '' : 's'}`}
            />
          ) : (
            <Chip variant='outlined' label='Not on call' />
          )}
          <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.75 }}>
            Photo, phone, role and time zone come from Microsoft Entra (synced
            hourly).
          </Typography>
          <TimeZoneLine userID={props.userID} />
        </Box>
      </Box>

      {err && (
        <MUIAlert severity='error' sx={{ mb: 2 }}>
          {err}
        </MUIAlert>
      )}

      <Stack spacing={2}>
        <Panel
          flush
          title='Contact methods'
          subtitle='Where alerts reach this person. Pause one instead of removing it.'
        >
          {!cms.length && (
            <Typography sx={{ px: 2.5, py: 2, color: c.muted }}>
              No phone number yet.
            </Typography>
          )}
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              {g.number && (
                <Box
                  sx={{
                    px: 2.5,
                    pt: 1.5,
                    pb: 0.5,
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    borderTop: `1px solid ${c.line}`,
                    '&:first-of-type': { borderTop: 0 },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                    {g.number}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: c.muted }}>
                    Mobile
                  </Typography>
                </Box>
              )}
              {g.cms.map((cm) => {
                const pause = pauses.data?.find((p) => p.cmID === cm.id)
                const unverified = cm.pending && cm.disabled && !pause
                const status = unverified ? (
                  <Chip
                    size='small'
                    color='warning'
                    variant='outlined'
                    label='Not verified'
                  />
                ) : cm.disabled ? (
                  <Chip
                    size='small'
                    color='warning'
                    icon={<PauseCircle />}
                    label={pausedLabel(pause)}
                  />
                ) : (
                  <Chip
                    size='small'
                    color='success'
                    variant='outlined'
                    label='Active'
                  />
                )
                return (
                  <Row
                    key={cm.id}
                    leading={<CMIcon cm={cm} />}
                    title={kindLabel(cm)}
                    subtitle={
                      [
                        PHONE_TYPES.includes(cm.dest.type)
                          ? ''
                          : cm.dest.displayInfo.text ||
                            cm.dest.displayInfo.error,
                        ruled.has(cm.id) ? '' : 'does not notify (no rule)',
                        pause?.reason ?? '',
                      ]
                        .filter(Boolean)
                        .join(' · ') || undefined
                    }
                    meta={status}
                    trailing={
                      canEdit ? (
                        <Stack direction='row' spacing={0.5}>
                          {unverified ? (
                            <Button
                              size='small'
                              onClick={() => setVerifyID(cm.id)}
                            >
                              Verify
                            </Button>
                          ) : cm.disabled ? (
                            <Button
                              size='small'
                              startIcon={<Replay />}
                              disabled={busy}
                              onClick={() =>
                                act(() =>
                                  post(`contact-methods/${cm.id}/resume`),
                                )
                              }
                            >
                              Resume
                            </Button>
                          ) : (
                            <Button
                              size='small'
                              startIcon={<PauseCircle />}
                              disabled={busy}
                              onClick={(e) =>
                                setMenu({ el: e.currentTarget, cm })
                              }
                            >
                              Pause
                            </Button>
                          )}
                          <Button
                            size='small'
                            disabled={cm.disabled}
                            onClick={() => setTestID(cm.id)}
                          >
                            Test
                          </Button>
                        </Stack>
                      ) : undefined
                    }
                  />
                )
              })}
            </React.Fragment>
          ))}
        </Panel>

        <Menu
          open={Boolean(menu)}
          anchorEl={menu?.el}
          onClose={() => setMenu(undefined)}
        >
          {[
            { minutes: 60, label: 'For 1 hour' },
            { minutes: 8 * 60, label: 'For 8 hours' },
            { minutes: 24 * 60, label: 'For 24 hours' },
            { minutes: 0, label: 'Until I resume' },
          ].map((o) => (
            <MenuItem
              key={o.minutes}
              onClick={() =>
                menu &&
                act(() =>
                  post(`contact-methods/${menu.cm.id}/pause`, {
                    minutes: o.minutes,
                  }),
                )
              }
            >
              <ListItemText primary={o.label} />
            </MenuItem>
          ))}
        </Menu>

        <Panel
          flush
          title='On call'
          subtitle='Services that would notify this person right now, and at which step.'
        >
          {!assignments.length && (
            <Typography sx={{ px: 2.5, py: 2, color: c.muted }}>
              Not on call for any service.
            </Typography>
          )}
          {assignments.map((a) => (
            <Row
              key={a.serviceID + a.stepNumber}
              to={`/services/${a.serviceID}`}
              title={a.serviceName}
              subtitle={`${a.escalationPolicyName} · step ${a.stepNumber + 1}`}
            />
          ))}
        </Panel>

        {canEdit && (
          <Panel title='More'>
            <Stack
              direction='row'
              spacing={1}
              sx={{ flexWrap: 'wrap', gap: 1 }}
              divider={<Divider orientation='vertical' flexItem />}
            >
              <AppLink to={`/users/${props.userID}/sessions`}>
                Signed-in sessions
              </AppLink>
              {me === props.userID && (
                <AppLink
                  to={`/users/${props.userID}/schedule-calendar-subscriptions`}
                >
                  Calendar subscriptions
                </AppLink>
              )}
            </Stack>
          </Panel>
        )}

        {!mobile && (
          <Suspense>
            <Typography
              component='h2'
              sx={{ fontFamily: serif, fontSize: 19, color: c.ink, mt: 1 }}
            >
              Shifts
            </Typography>
            <UserShiftsCalendar userID={props.userID} />
          </Suspense>
        )}
      </Stack>
    </Page>
  )
}
